import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TriangleAlert, Zap } from 'lucide-react';
import { z } from 'zod';
import { OptionSelect } from '@/components/common/OptionSelect';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { formatCount, formatTaka } from '@/utils/number';
import { pcmpRequestCategoriesForSubmitQueryOptions } from '../../api/pcmpRequests.api';
import {
  PCMP_CHANNEL_LABELS,
  PCMP_MKT_COMMENT_MAX,
  PCMP_MKT_PROOF_ACCEPT,
} from '../../constants/pcmpProposal.constants';
import {
  PCMP_NO_ACTION_PLAN_LABEL,
  PCMP_REQUEST_APPLIES_TO_LABELS,
  PCMP_RETURN_CATEGORY_CODE,
} from '../../constants/pcmpRequest.constants';
import { useSubmitPcmpRequests } from '../../hooks/useSubmitPcmpRequests';
import type { PcmpChannel } from '../../types/pcmpProposal.types';
import type { PcmpSkippedProposal } from '../../types/pcmpQueue.types';
import type { PcmpRequestSelection, PcmpRequestSubmitResult } from '../../types/pcmpRequest.types';
import type { PcmpRequestAppliesTo } from '../../types/pcmpRequestCategory.types';
import { pcmpAmountSchema } from '../../utils/pcmpProposalEditForm';
import { extractSkippedProposals } from '../../utils/pcmpRequestErrors';
import { pcmpUploadFileSchema } from '../../utils/pcmpUploadFile';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpStatusPill } from '../PcmpProposalDetail/PcmpStatusPill';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';

/** A picked proposal as the list showed it: a Payment Return needs its channel. */
export interface PcmpRequestSubmitItem {
  proposalId: number;
  refNo: string;
  paymentSource: PcmpChannel;
}

const RETURN_MESSAGES = {
  NEEDS_IDS: 'A Payment Return request names its proposals: tick them one by one.',
  MIXED_CHANNELS:
    'A Payment Return request is for one payment channel: do not mix Accounts and Marketing payouts.',
  SLIP_REQUIRED: 'A bank deposit slip is required to return an Accounts payment.',
} as const;

const returnAmountSchema = pcmpAmountSchema('Amount', { positive: true });

const isMixedChannels = (items: readonly PcmpRequestSubmitItem[]) =>
  new Set(items.map((item) => item.paymentSource)).size > 1;

/**
 * Legacy LDS-766: returning Marketing payouts may go without a comment; every
 * other request needs one (the API applies the same rule).
 */
const isCommentOptional = (
  isReturn: boolean,
  items: readonly PcmpRequestSubmitItem[] | undefined,
): boolean =>
  isReturn &&
  items !== undefined &&
  items.length > 0 &&
  items.every((item) => item.paymentSource === 'MARKETING');

/**
 * A Payment Return (LDS-765) also takes, per proposal, an amount (blank: all
 * that is left) and the deposit slip, required for an Accounts payout.
 */
function submitFormSchema(
  returnCategoryIds: ReadonlySet<number>,
  items: readonly PcmpRequestSubmitItem[] | undefined,
) {
  return z
    .object({
      categoryId: z.number({ message: 'Select a request category.' }),
      comment: z
        .string()
        .trim()
        .max(PCMP_MKT_COMMENT_MAX, `Comment must be ${PCMP_MKT_COMMENT_MAX} characters or fewer.`),
      returns: z.array(z.object({ amount: z.string(), slip: pcmpUploadFileSchema })),
    })
    .superRefine((values, ctx) => {
      const isReturn = returnCategoryIds.has(values.categoryId);
      if (values.comment === '' && !isCommentOptional(isReturn, items)) {
        ctx.addIssue({ code: 'custom', path: ['comment'], message: 'A comment is required.' });
      }
      if (!isReturn) return;
      if (!items) {
        ctx.addIssue({ code: 'custom', path: ['categoryId'], message: RETURN_MESSAGES.NEEDS_IDS });
        return;
      }
      if (isMixedChannels(items)) {
        ctx.addIssue({
          code: 'custom',
          path: ['categoryId'],
          message: RETURN_MESSAGES.MIXED_CHANNELS,
        });
        return;
      }
      items.forEach((item, index) => {
        const entry = values.returns[index];
        if (!entry) return;
        if (entry.amount.trim() !== '') {
          const parsed = returnAmountSchema.safeParse(entry.amount);
          if (!parsed.success) {
            ctx.addIssue({
              code: 'custom',
              path: ['returns', index, 'amount'],
              message: parsed.error.issues[0]?.message ?? 'Enter a valid amount.',
            });
          }
        }
        if (item.paymentSource === 'ACCOUNTS' && entry.slip === null) {
          ctx.addIssue({
            code: 'custom',
            path: ['returns', index, 'slip'],
            message: RETURN_MESSAGES.SLIP_REQUIRED,
          });
        }
      });
    });
}

type SubmitFormInput = z.input<ReturnType<typeof submitFormSchema>>;
type SubmitFormValues = z.output<ReturnType<typeof submitFormSchema>>;

export interface PcmpRequestSubmitTarget {
  count: number;
  amount: number;
  selection: PcmpRequestSelection;
  /** The tab the proposals come from; left out for one proposal whose tab is not known. */
  appliesTo?: PcmpRequestAppliesTo | undefined;
  /** The ticked proposals; left out for "select all matching", which a Payment Return refuses. */
  items?: readonly PcmpRequestSubmitItem[] | undefined;
}

interface PcmpRequestSubmitDialogProps {
  target: PcmpRequestSubmitTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  /** Called once requests were raised, so the caller can clear its selection. */
  onSubmitted: () => void;
  onClose: () => void;
}

interface Outcome {
  title: string;
  summary: string;
  skipped: PcmpSkippedProposal[];
}

/**
 * Legacy RequestSubmit: raise one request category on the picked proposals, with a
 * comment (optional only when returning Marketing payouts, LDS-766). The API
 * re-checks each one; those that changed since the
 * list was loaded, or a return asking for more than is left, are left out and listed.
 */
export function PcmpRequestSubmitDialog({
  target,
  refNoOf,
  onSubmitted,
  onClose,
}: PcmpRequestSubmitDialogProps) {
  const { items } = target;
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const { data: categories, isLoading: isLoadingCategories } = useQuery(
    pcmpRequestCategoriesForSubmitQueryOptions(target.appliesTo),
  );
  const returnCategoryIds = useMemo(
    () =>
      new Set(
        (categories ?? [])
          .filter((category) => category.code === PCMP_RETURN_CATEGORY_CODE)
          .map((category) => category.id),
      ),
    [categories],
  );
  const schema = useMemo(
    () => submitFormSchema(returnCategoryIds, items),
    [returnCategoryIds, items],
  );
  const form = useForm<SubmitFormInput, unknown, SubmitFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      comment: '',
      returns: (items ?? []).map(() => ({ amount: '', slip: null })),
    },
  });

  const { mutate, isPending, error, reset } = useSubmitPcmpRequests(
    (result: PcmpRequestSubmitResult) => {
      onSubmitted();
      if (result.skipped.length > 0) {
        setOutcome({
          title: `${formatCount(result.requested, 'request')} raised: ${result.category.name}`,
          summary: 'The proposals below were left out, for the reason given.',
          skipped: result.skipped,
        });
      } else {
        onClose();
      }
    },
  );

  // The API refuses the whole call (409) when none could be requested and says why per proposal.
  const refusedSkips = extractSkippedProposals(error);

  const options = (categories ?? []).map((category) => ({
    id: category.id,
    name:
      target.appliesTo === undefined
        ? `${category.name} (${PCMP_REQUEST_APPLIES_TO_LABELS[category.appliesTo]})`
        : category.name,
  }));
  const categoryId = form.watch('categoryId');
  const picked = categories?.find((category) => category.id === categoryId);
  const isReturn = picked?.code === PCMP_RETURN_CATEGORY_CODE;
  const commentOptional = isCommentOptional(isReturn, items);
  const returnBlock = !isReturn
    ? null
    : !items
      ? RETURN_MESSAGES.NEEDS_IDS
      : isMixedChannels(items)
        ? RETURN_MESSAGES.MIXED_CHANNELS
        : null;

  const onSubmit = form.handleSubmit((values) => {
    const payload = {
      categoryId: values.categoryId,
      comment: values.comment === '' ? undefined : values.comment,
      ...target.selection,
    };
    if (!isReturn || !items) {
      mutate({ payload });
      return;
    }
    mutate({
      payload,
      returns: items.map((item, index) => {
        const entry = values.returns[index];
        const amount = entry?.amount.trim() ?? '';
        return {
          proposalId: item.proposalId,
          amount: amount === '' ? undefined : Number(amount),
          slip: entry?.slip ?? null,
        };
      }),
    });
  });

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title={outcome.title}
        summary={outcome.summary}
        skipped={outcome.skipped}
        refNoOf={refNoOf}
        onClose={onClose}
      />
    );
  }

  if (refusedSkips) {
    return (
      <PcmpOutcomeDialog
        title="Nothing could be requested"
        summary="None of the selected proposals can be requested right now. The reason is listed for each."
        skipped={refusedSkips}
        refNoOf={refNoOf}
        onClose={() => {
          reset();
          onClose();
        }}
      />
    );
  }

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title="Raise request"
        description={
          target.appliesTo ? `${PCMP_REQUEST_APPLIES_TO_LABELS[target.appliesTo]} proposals` : ''
        }
        icon={Zap}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Raise request"
        submitDisabled={returnBlock !== null}
        pendingLabel="Raising..."
      >
        <div className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-xs text-muted-foreground">Requesting for</span>
          <span className="font-medium">
            {formatCount(target.count, 'proposal')} · {formatTaka(target.amount)}
          </span>
        </div>

        <FormField
          control={form.control}
          name="categoryId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Request category <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <OptionSelect
                  id="pcmp-request-category"
                  options={options}
                  isLoading={isLoadingCategories}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  placeholder={options.length === 0 ? 'No category available' : 'Select a category'}
                  className="w-full"
                />
              </FormControl>
              {picked ? (
                <FormDescription>
                  Approving it:{' '}
                  {picked.actionPlanKey ? picked.actionPlanLabel : PCMP_NO_ACTION_PLAN_LABEL}.
                </FormDescription>
              ) : null}
              <FormMessage />
            </FormItem>
          )}
        />

        {returnBlock ? (
          <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <span>{returnBlock}</span>
          </div>
        ) : null}

        {isReturn && items && !returnBlock ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Leave the amount blank to return everything left when the request is approved.
            </p>
            <div className="flex max-h-80 flex-col gap-3 overflow-y-auto pr-1">
              {items.map((item, index) => (
                <div
                  key={item.proposalId}
                  className="grid grid-cols-1 gap-3 rounded-lg border px-4 py-3 sm:grid-cols-2"
                >
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <span className="font-mono font-medium">{item.refNo}</span>
                    <PcmpStatusPill tone="neutral">
                      {PCMP_CHANNEL_LABELS[item.paymentSource]}
                    </PcmpStatusPill>
                  </div>
                  <FormField
                    control={form.control}
                    name={`returns.${index}.amount`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Amount</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="All that is left"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`returns.${index}.slip`}
                    render={({ field: { onChange, onBlur, name, ref } }) => (
                      <FormItem>
                        <FormLabel>
                          Bank deposit slip
                          {item.paymentSource === 'ACCOUNTS' ? (
                            <span className="text-destructive"> *</span>
                          ) : (
                            <span className="text-muted-foreground"> (optional)</span>
                          )}
                        </FormLabel>
                        <FormControl>
                          <Input
                            ref={ref}
                            name={name}
                            type="file"
                            accept={PCMP_MKT_PROOF_ACCEPT}
                            onBlur={onBlur}
                            onChange={(event) => onChange(event.target.files?.[0] ?? null)}
                            className="cursor-pointer"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required={!commentOptional}
          placeholder="Why this is needed"
          description={
            commentOptional
              ? 'Optional when returning Marketing payouts. Recorded on each proposal’s timeline.'
              : "Recorded on each proposal's timeline."
          }
        />
      </PcmpEditDialog>
    </Form>
  );
}
